/**
 * Mountain tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const mountainTattoosPack: ToilePack = {
    slug: 'mountain-tattoos',
    category: 'tattoo',
    name: 'Mountain tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'circle', label: 'Mountains in circle', subject: 'a range of three peaks inside a circle, a sun above the middle peak and a row of pines along the base'},
        {key: 'forest', label: 'Mountain and forest', subject: 'a wide mountain range with snow on the peaks, a pine forest in front and a river winding toward the viewer'},
        {key: 'line', label: 'One line range', subject: 'a mountain range drawn as one continuous line, five peaks of different heights, nothing else'},
    ],
    styles: [
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'blackwork', label: 'Blackwork', style: 'a blackwork tattoo drawing, heavy solid black masses with the details cut out of them as bare paper', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin, the dots reading cleanly'},
    ],
    directions: ['a lake', 'green ink', 'calf'],
    directionHint: 'try: a lake',
    seo: {
        title: 'Mountain tattoo designs: circle range, forest, one line',
        metaDescription: 'Nine mountain tattoo designs: a range in a circle, a range with a pine forest and river and a single-line range, in three styles.',
        h1: 'Mountain tattoo designs: range in a circle, mountain and forest, one-line range',
        tagline: 'Three mountain ranges, each drawn in fine line, blackwork and dotwork on white paper.',
        intro: 'Mountain tattoo designs, worn by hikers, climbers and people who keep a place they love with them, on the forearm, the inner arm or the calf. The nine sheets show a three-peak range in a circle, a range with a pine forest and river, and a range drawn as one line. Each is drawn as fine line, blackwork and dotwork. Type a different landscape or twist, such as a lake, and the same styles can be asked for.',
        useCases: [
            'a forearm piece for a place kept in memory',
            'a single-line range on the inner arm',
            'a circle design for the calf',
            'a printed sheet to compare styles',
        ],
        faq: [
            {q: 'What is on the page?', a: 'Nine mountain designs: a range in a circle, a range with forest and river, and a one-line range, each in three styles.'},
            {q: 'Is this a design on skin?', a: 'No. Each design is a drawing on white paper, for taking to a studio or printing.'},
            {q: 'Can I ask for a lake or a different landscape?', a: 'Yes. Type a different landscape or twist, such as a lake; the result is a new drawing, not a guaranteed match.'},
            {q: 'What is the difference between the styles?', a: 'Fine line is one uniform hairline with little shading. Blackwork uses heavy solid black masses. Dotwork builds the shadows from dots.'},
        ],
    },
}
